/**
 * Composable para optimización de recursos (imágenes, videos)
 * Gestiona la carga eficiente y límites de caché
 */

import { onUnmounted } from 'vue'

// Cache de imágenes precargadas
const imageCache = new Map<string, boolean>()
const preloadedVideoCache = new Set<string>()
const pendingVideoPreloads = new Map<string, Promise<void>>()

// Configuración
const MAX_CACHE_SIZE = 50 // Máximo de imágenes en memoria
const CACHE_CLEANUP_THRESHOLD = 60 // Limpiar cuando alcance este número

export function useResourceOptimization() {
  // Lista de objetos multimedia para limpiar
  const loadedMedia: Array<HTMLImageElement | HTMLVideoElement> = []

  /**
   * Precarga una imagen de forma optimizada
   */
  const preloadImage = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Si ya está en caché, resolver inmediatamente
      if (imageCache.has(src)) {
        resolve()
        return
      }

      // Limpiar caché si está muy llena
      if (imageCache.size >= CACHE_CLEANUP_THRESHOLD) {
        cleanupCache()
      }

      const img = new Image()
      
      img.onload = () => {
        imageCache.set(src, true)
        resolve()
      }
      
      img.onerror = () => {
        reject(new Error(`Failed to load image: ${src}`))
      }
      
      img.src = src
      loadedMedia.push(img)
    })
  }

  /**
   * Precarga múltiples imágenes
   */
  const preloadImages = (sources: string[]): Promise<void[]> => {
    return Promise.all(sources.map(src => preloadImage(src)))
  }

  /**
   * Limpia las imágenes más antiguas de la caché
   */
  const cleanupCache = () => {
    if (imageCache.size > MAX_CACHE_SIZE) {
      const entriesToDelete = imageCache.size - MAX_CACHE_SIZE
      const keys = Array.from(imageCache.keys())
      
      // Eliminar las primeras entradas (más antiguas)
      for (let i = 0; i < entriesToDelete; i++) {
        imageCache.delete(keys[i])
      }
      
      console.log(`Cache cleaned: ${entriesToDelete} images removed`)
    }
  }

  /**
   * Limpia todas las referencias a imágenes precargadas
   */
  const cleanup = () => {
    loadedMedia.forEach(media => {
      if (media instanceof HTMLImageElement) {
        media.onload = null
        media.onerror = null
        media.src = ''
        return
      }

      media.pause()
      media.removeAttribute('src')
      media.load()
    })
    loadedMedia.length = 0
  }

  /**
   * Precarga un video en el navegador
   */
  const preloadVideo = (src: string): Promise<void> => {
    if (preloadedVideoCache.has(src)) {
      return Promise.resolve()
    }

    const pendingPreload = pendingVideoPreloads.get(src)
    if (pendingPreload) {
      return pendingPreload
    }

    const preloadPromise = new Promise<void>((resolve) => {
      const video = document.createElement('video')
      video.preload = 'auto'
      video.muted = true
      video.playsInline = true

      const complete = () => {
        preloadedVideoCache.add(src)
        pendingVideoPreloads.delete(src)
        resolve()
      }
      
      video.addEventListener('loadeddata', () => {
        console.log('✅ Video precargado:', src)
        complete()
      }, { once: true })
      
      video.addEventListener('error', (e) => {
        console.warn('⚠️ Error precargando video:', src, e)
        pendingVideoPreloads.delete(src)
        resolve() // Resolver de todos modos para no bloquear
      }, { once: true })
      
      video.src = src
      video.load()
      
      // Guardar referencia para limpieza
      loadedMedia.push(video)
    })

    pendingVideoPreloads.set(src, preloadPromise)
    return preloadPromise
  }

  /**
   * Optimiza un video para carga eficiente
   */
  const optimizeVideo = (videoElement: HTMLVideoElement) => {
    if (!videoElement) return

    // Configuración de optimización
    videoElement.preload = 'metadata' // Solo cargar metadata inicialmente
    videoElement.playsInline = true
    videoElement.muted = true

    // Pausar cuando no esté visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            videoElement.play().catch(() => {
              // Ignorar errores de autoplay
            })
          } else {
            videoElement.pause()
          }
        })
      },
      { threshold: 0.5 }
    )

    observer.observe(videoElement)

    // Limpiar observer al desmontar
    onUnmounted(() => {
      observer.disconnect()
    })
  }

  /**
   * Obtiene el estado de la caché
   */
  const getCacheStats = () => {
    return {
      size: imageCache.size,
      maxSize: MAX_CACHE_SIZE,
      cleanupThreshold: CACHE_CLEANUP_THRESHOLD
    }
  }

  /**
   * Limpia manualmente la caché completa
   */
  const clearCache = () => {
    imageCache.clear()
    cleanup()
    console.log('Image cache cleared')
  }

  // Limpiar al desmontar el componente
  onUnmounted(() => {
    cleanup()
  })

  return {
    preloadImage,
    preloadImages,
    preloadVideo,
    optimizeVideo,
    getCacheStats,
    clearCache,
    cleanup
  }
}
